import React from "react";
import addDays from "date-fns/addDays";
import axios, { AxiosResponse } from "axios";
import moment from 'moment';
import { AlertColor, Button, IconButton, InputBase, Link, Paper, TextField, useMediaQuery} from "@mui/material";
import { Add, AddBusiness, ContentCopy, Replay, Search } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { BaseControl, DatePickerControl, DropDownComboControl, DropDownComboOption, GenericFilter } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { QuotationFilter } from "../../../models/classes/QuotationFilter";
import { CustomTablePagination } from "../../component/CustomPagination";
import { Quotation } from "./QuotationAccessories";
import { GetQuotationRegion, QuotationSingle, QuotationSingleHandle } from "./QuotationSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { BranchInfo } from "../branches/BranchAccessories";
import { Pad } from "../../../common/Formatter";
import { getDefaultRegion, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { Navigate, useNavigate } from "react-router-dom";
import { PurchaseOrderSingle, PurchaseOrderSingleHandle } from "../purchase-orders/PurchaseOrderSingle";
import { ManufacturerCombo } from "../manufacturers/ManufacturerAccessories";
import { PurchaseOrder } from "../purchase-orders/PurchaseOrderAccessories";
import { defaultContactPerson, defaultPoRegions, deliveryAddress, deliveryAddressSS, deliveryAttn, deliveryPhone, deliveryPhoneSS, deliveryTo, deliveryToSS } from "../../../common/Constants";
import { OpenMode } from "../../../common/Enums";

export function QuotationList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<QuotationSingleHandle>(null);
    
	const singlePoModalRef = React.useRef<PurchaseOrderSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

	const [branchInfo, setBranchInfo] = React.useState<BranchInfo[]>([]);

	const [manufacturers, setManufacturers] = React.useState<DropDownComboOption[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        endUser: 0,
        createdFrom: addDays(new Date(), -60),
        createdTo: new Date(),
		sales: 0,
        region: "ALL",
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData() {
		setLoading(true);
        let filter = new QuotationFilter({
			QuotationNo: searchData.searchText,
			CustomerID: searchData.customer % 100000,
			EndUserID: searchData.endUser,
			From: moment(searchData.createdFrom).format(),
			To: moment(searchData.createdTo).format(),
			StaffID: Number(searchData.sales),
			Region: searchData.region,
			Reader: ""
		});

		axios.post<Quotation[]>('Api/V1/Quotation/Search', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.quotationNo;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.quotationNo > b.quotationNo) { return -1; }
						if (a.quotationNo < b.quotationNo) { return 1; }
					}
					return 0;					
				}))
				setAllData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.quotationNo > b.quotationNo) { return -1; }
						if (a.quotationNo < b.quotationNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setAllData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function LoadManufacturers() {
		let loadedManufacturers: DropDownComboOption[] = [];
		axios.get<ManufacturerCombo[]>('Api/V1/Manufacturer/Combo', {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.map((e) => loadedManufacturers.push({ name: e.name, value: e.manufacturerID.toString() }));
				setManufacturers(loadedManufacturers);
			},
				(error) => {
					console.log(error);
					setLoading(false);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				setLoading(false);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});	
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		let endUsersDropDownOption: DropDownComboOption[] = [];
		props.endUsers.map(e => endUsersDropDownOption.push({ name: e.endUserName, value: e.endUserId.toString() }));
		setControls([
			{
				key: 'createdFrom',
				title: 'Created From',
				dateValue: addDays(new Date(), -7)
			} as DatePickerControl,
			{
				key: 'createdTo',
				title: 'Created To',
				dateValue: new Date()
			} as DatePickerControl,
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl,
			{
				key: 'endUser',
				title: 'End User',
				dropdownOptions: endUsersDropDownOption
			} as DropDownComboControl,
			{
				key: 'region',
				title: 'Region',
				dropdownOptions: props.regions
			} as DropDownComboControl,
			{
				key: 'sales',
				title: 'Sales',
				dropdownOptions: props.sales
			} as DropDownComboControl
		]);
		LoadManufacturers();
		LoadData();
    }

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'quotationNo', headerName: 'Quotation No', flex: 1, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString());
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'customerName', headerName: 'Customer', flex: 4, type: 'string' },
		{ field: 'endUser', headerName: 'End User', flex: 3, type: 'string' },
		{ field: 'createDate', headerName: 'Create Date', flex: 1, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{ field: 'staff', headerName: 'Sales', flex: 1, type: 'string' },
		{ field: 'createdBy', headerName: 'Created By', flex: 1, type: 'string' },
	];

	const [gridData, setGridData] = React.useState<Quotation[]>([]);
    const [allData, setAllData] = React.useState<Quotation[]>([]);
	const selectedData = React.useRef<Quotation[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject()}>Create</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<ContentCopy />} onClick={() => CopyObject()}>Copy</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px", minWidth: "125px" }} startIcon={<AddBusiness />} onClick={() => CreatePo()}>Create PO</Button>
				<div style={{ flexGrow: 1 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Replay />} onClick={() => Initialise()}>Reload</Button>
			</GridToolbarContainer>
		);
	}

	function NewObject() {
		axios.get<any[]>(`Api/V1/Branch`, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				setBranchInfo(result.data);

				let defaultBranch: BranchInfo | undefined = result.data.find(b => b.region === getDefaultRegion());
				if (defaultBranch)
				{
					let quotation: Quotation = {
						id: "",
						quotationNo: "QT" + defaultBranch.japanYear + defaultBranch.region + Pad(defaultBranch.quotationNo, 5),
						revisionNo: "0",
						custLangID: 1,
						customerID: 0,
						customerName: "",
						languageID: 1,
						endUserID: 0,
						endUser: "",
						createDate: moment(new Date()).format(),
						staffID: 0,
						staff: "",
						createdBy: "",
						attention: "",
						phone: "",
						fax: "",
						mobile: "",
						email: "",
						shipDate: "Around 2 or 3 weeks",
						deliveryTerm: "C.I.F. HK ",
						paymentTerm: "30 days after delivery",
						currency: "JPY",
						exRate: 1,
						remark1: "注意: 如果貴公司須要由日本--香港--中國, 香港到中國的運貨, 將由貴公司負責, 但不須要做報關, 敬希垂注!",
						remark2: "The price quoted is net price per piece and will be valid for 30 days only.",
						remark3: "If order amount is less than RMB10000, please pick up the goods at Musashi Eng. office. 如订单金额少於人民幣一万元, 请自行派人到武藏办公室取货。",
						quotationItems: [],
						loginName: "",
						status: "ACT"
					}
					singleModalRef.current?.Initialise(OpenMode.NEW, quotation);
				}

			},
				(error) => {
					setBranchInfo([]);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function CopyObject() {
		if (selectedData.current.length != 1)
		{
			showSnackBar("Please select one row to copy.", "", "warning");
			return;
		}
		
		let quotationNo = selectedData.current[0].quotationNo;

		setLoading(true);

		axios.all([
			axios.get(`Api/V1/Quotation/` + quotationNo, {
				headers: {
				  'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const quotationResult: AxiosResponse<Quotation> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let branch: BranchInfo | undefined = branchInfoResult.data.find(b => b.region === GetQuotationRegion(quotationNo));
			if (branch)
			{
				quotationResult.data.quotationNo = "QT" + branch.japanYear + branch.region + Pad(branch.quotationNo, 5);
				quotationResult.data.createDate = moment(new Date()).format();
				quotationResult.data.createdBy=''; //createBy cannot be null when save
				quotationResult.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.COPY, quotationResult.data);
				setLoading(false);
			}			
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		});
	}

	function EditObject(quotationNo: string) {
        setLoading(true);
		axios.get<Quotation>('Api/V1/Quotation/' + quotationNo, {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				result.data.createdBy=''; //createBy cannot be null when save
				result.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");

			});
	}

	
	function CreatePo() {
		if (selectedData.current.length == 0)
		{
			showSnackBar("Please select at least one row to create PO.", "", "warning");
			return;
		}
		if (selectedData.current.length > 1)
		{
			var customerId = selectedData.current[0].customerID;
			let quotationOfDifferentCustomer = selectedData.current.find(x => x.customerID != customerId);
			if (quotationOfDifferentCustomer)
			{
				showSnackBar("Please select quotations of the same customer to create PO.", "", "warning");
				return;
			}
		}

		let quotationNo = selectedData.current[0].quotationNo;
		let quotationNos = Array.prototype.map.call(selectedData.current, q => q.quotationNo).toString();

		setLoading(true);
        const params = { quotationNos:  quotationNos};

		axios.all([
			axios.get(`Api/V1/PurchaseOrder/CreateByQuotation`, {
				params,
				headers: {
				  'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const poResult: AxiosResponse<PurchaseOrder> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let branch: BranchInfo | undefined = branchInfoResult.data.find(b => b.region === GetQuotationRegion(quotationNo));
			if (branch)
			{
				poResult.data.poNo = "PO" + branch.japanYear + branch.region + Pad(branch.poNo, 5);
				poResult.data.createDate = moment(new Date()).format();
				poResult.data.createdBy = ''; //createBy cannot be null when save
				poResult.data.status = 'ACT'; //status cannot be null when save
				poResult.data.deliveryTo = branch.region == "SS" ? deliveryToSS : deliveryTo;
				poResult.data.deliveryAddress = branch.region == "SS" ? deliveryAddressSS : deliveryAddress;
				poResult.data.deliveryPhone = branch.region == "SS" ? deliveryPhoneSS : deliveryPhone;
				poResult.data.deliveryAttention = deliveryAttn;
				poResult.data.attention = defaultContactPerson;
				singlePoModalRef.current?.Initialise(OpenMode.COPY, poResult.data);
			}				
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		});
	}

    function GetPurchaseOrderRegion(quotationNo: string) {
		let region = quotationNo.substring(4, 6);
		if (defaultPoRegions.findIndex(x => x == region) > -1)
		  return region;
		else
		  return "HK";  
	}

    function DoSearch(event: React.FormEvent<any>) {
		if (event)
        	event.preventDefault();
    
		LoadData();
    }

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Quotation List" />
			<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="Quotation No" />
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					checkboxSelection
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					onSelectionModelChange={(selectionModel) => {
						const ids = new Set(selectionModel);
						const selectedRows = gridData.filter((row) => ids.has(row.quotationNo));
						selectedData.current = selectedRows;
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
				/>
			</div>

			<QuotationSingle
				ref={singleModalRef}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, quotation: Quotation) => {
					if (saved) {
						let tempData = [...allData];
						let tempGridData = [...gridData];
						quotation.id = quotation.quotationNo;

						let allIndex = tempData.findIndex(q => q.quotationNo === quotation.quotationNo);
						let gridIndex = tempGridData.findIndex(q => q.quotationNo === quotation.quotationNo);

						if (allIndex === -1)
							tempData.push(quotation);
						else
							tempData[allIndex] = quotation;	

						if (gridIndex === -1)
							tempGridData.push(quotation);
						else
							tempGridData[gridIndex] = quotation;	

						setAllData(tempData.sort((a, b) => {
							if (a.createDate > b.createDate) { return -1; }
							if (a.createDate < b.createDate) { return 1; }
							if (a.createDate === b.createDate)
							{
								if (a.quotationNo > b.quotationNo) { return -1; }
								if (a.quotationNo < b.quotationNo) { return 1; }
							}
							return 0;
						}));

						setGridData(tempGridData.sort((a, b) => {
							if (a.createDate > b.createDate) { return -1; }
							if (a.createDate < b.createDate) { return 1; }
							if (a.createDate === b.createDate)
							{
								if (a.quotationNo > b.quotationNo) { return -1; }
								if (a.quotationNo < b.quotationNo) { return 1; }
							}
							return 0;
						}));
					}
				} } 
			/>

			<PurchaseOrderSingle
				ref={singlePoModalRef}
				manufacturers={manufacturers}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, po: PurchaseOrder) => {
					if (saved) {
						navigate("/app/purchaseorder");
					}
				} } 
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

